import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner, PageFooter } from '../shared/components';
import { pageIntro } from '../shared/components/Narrative';
import { infoIcon } from '../shared/components/InfoIcon';

type Tab = 'propensity' | 'why' | 'pacing' | 'financials' | 'data';
type DataSource = 'overview' | 'airroi' | 'pricelabs' | 'reservations' | 'calendar' | 'reviews' | 'tasks';
const DATA_SOURCES: DataSource[] = ['overview', 'airroi', 'pricelabs', 'reservations', 'calendar', 'reviews', 'tasks'];
const ANALYSIS_TABS: Tab[] = ['pacing', 'financials'];

// Per-property raw-data explorer across all sources (AirROI, PriceLabs, Hostaway).
export class DataExplorerPage {
  private container: HTMLElement;
  private listings: any[] = [];
  private selectedId = '';
  private data: any = null;
  private tab: Tab = 'propensity';
  private dataSource: DataSource = 'overview';
  // Old deep-link ids (fix buttons, sessionStorage) → the 5-tab world.
  private setTab(raw: string): void {
    if (DATA_SOURCES.includes(raw as DataSource)) { this.tab = 'data'; this.dataSource = raw as DataSource; return; }
    if (raw === 'forecast') { this.tab = 'pacing'; return; }
    if (raw === 'report') { this.tab = 'financials'; return; }
    if (['propensity', 'why', 'pacing', 'financials', 'data'].includes(raw)) this.tab = raw as Tab;
  }
  private calDays = 120;
  private loading = false;
  // Analysis (pacing/forecast/financials/report) state
  private analysis: any = null;
  private marketPct = 50;                            // market percentile
  private pctInit = false;                            // has marketPct been set from the property's stored tier?
  private aYear = new Date().getFullYear();
  private aMonth = new Date().getMonth() + 1;        // focus month for pacing
  private propensity: any = null;                     // booking-probability calendar

  constructor(container: HTMLElement) { this.container = container; }

  // ── formatting helpers ─────────────────────────────────────
  // Every displayed value funnels through one of these — they all fall back to
  // an em dash when the value is missing / non-finite, never 'NaN'/'undefined'.
  private num(v: any): string { return v == null || v === '' ? '—' : (typeof v === 'number' ? v.toLocaleString() : String(v)); }
  private money(v: any): string { const n = parseFloat(v); return Number.isFinite(n) ? '$' + Math.round(n).toLocaleString() : '—'; }
  private pct(v: any): string { const n = parseFloat(v); return Number.isFinite(n) ? `${Math.round(n)}%` : '—'; }
  // Ratio (MPI / RevPAR index) → fixed-2 or em dash.
  private ratio(v: any, digits = 2): string { const n = Number(v); return Number.isFinite(n) ? n.toFixed(digits) : '—'; }
  // Bar width: clamp a possibly-missing percentage to 0–100 for safe inline use.
  private barPct(v: any): number { const n = Number(v); return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0; }
  private cell(v: any): string {
    if (v == null) return '<span style="color:var(--text-muted);">—</span>';
    if (typeof v === 'boolean') return v ? '✓' : '✗';
    if (typeof v === 'object') return `<code style="font-size:11px;">${Formatters.escapeHtml(JSON.stringify(v))}</code>`;
    return Formatters.escapeHtml(String(v));
  }
  private th(t: string, align = 'left'): string {
    return `<th style="padding:8px 12px; text-align:${align}; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.04em; border-bottom:1px solid var(--border); background:var(--bg-subtle); position:sticky; top:0; white-space:nowrap;">${t}</th>`;
  }
  // Wrap a wide table so it scrolls horizontally instead of overflowing the page.
  private tableWrap(inner: string, maxHeight = ''): string {
    const mh = maxHeight ? `max-height:${maxHeight}; overflow:auto;` : '';
    return `<div class="u-table-wrap" style="${mh}"><table style="width:100%; border-collapse:collapse; min-width:520px;">${inner}</table></div>`;
  }
  // Generic two-column key/value table for a raw row.
  private kvTable(obj: any, exclude: string[] = []): string {
    if (!obj) return '<div style="color:var(--text-muted); font-size:13px; padding:8px 0;">No data.</div>';
    const rows = Object.entries(obj)
      .filter(([k]) => !exclude.includes(k) && !k.startsWith('_'))
      .map(([k, v]) => `<tr style="border-bottom:1px solid var(--border-soft);">
        <td style="padding:6px 12px; font-size:12px; color:var(--text-muted); white-space:nowrap;">${Formatters.escapeHtml(k)}</td>
        <td style="padding:6px 12px; font-size:13px; word-break:break-word;">${this.cell(v)}</td></tr>`).join('');
    return `<div class="u-table-wrap"><table style="width:100%; border-collapse:collapse;"><tbody>${rows}</tbody></table></div>`;
  }
  private section(title: string, body: string): string {
    return `<div style="margin-bottom:18px;"><div style="font-size:13px; font-weight:700; color:var(--text); margin-bottom:8px;">${title}</div>${body}</div>`;
  }
  // Neutral inline-empty message used inside a tab body.
  private empty(msg: string): string {
    return `<div style="color:var(--text-muted); font-size:13px; padding:8px 0;">${msg}</div>`;
  }
  // Full-panel placeholder (no property selected / pre-load state).
  private placeholder(msg: string): string {
    return `<div class="pf-section" style="padding:40px; text-align:center; color:var(--text-muted); font-size:13px;">${msg}</div>`;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="page-content">
        <style>
          .de-toolbar { padding:14px 18px; margin-bottom:16px; display:flex; align-items:flex-end; gap:12px; flex-wrap:wrap; }
          .de-field { display:flex; flex-direction:column; gap:4px; min-width:0; }
          .de-field label { font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
          .de-select { padding:8px 12px; border:1px solid var(--border); border-radius:8px; font-size:13px; background:#fff; color:var(--text); cursor:pointer; font-family:inherit; max-width:100%; }
          .de-select:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
          #de-property { min-width:300px; }
          #de-tabs { display:none; gap:6px; flex-wrap:wrap; margin-bottom:14px; }
          .de-tab { padding:7px 14px; border-radius:8px; font-size:13px; cursor:pointer; border:1px solid var(--border); background:#fff; color:var(--text); transition:all .12s; white-space:nowrap; }
          .de-tab:hover { border-color:#d1d5db; background:var(--bg-subtle); }
          .de-tab.active { font-weight:600; border-color:var(--text); background:var(--text); color:#fff; }
          .de-tab:focus-visible { outline:none; box-shadow:var(--focus-ring); }
          .de-tab-count { opacity:.75; }
          .de-cols { display:flex; gap:24px; flex-wrap:wrap; }
          .de-col { flex:1; min-width:240px; }
          .de-cards { display:flex; gap:14px; flex-wrap:wrap; }
          .de-toggle-btn { padding:3px 9px; border-radius:6px; font-size:11px; cursor:pointer; border:1px solid var(--border); background:#fff; color:var(--text); transition:all .12s; }
          .de-toggle-btn:hover { border-color:#d1d5db; background:var(--bg-subtle); }
          .de-toggle-btn:focus-visible { outline:none; box-shadow:var(--focus-ring); }
          .de-toggle-btn.active { border-color:var(--text); background:var(--text); color:#fff; }
          .de-toggle-btn.active.alt { border-color:var(--primary); background:var(--primary); }
          @media (max-width: 640px) {
            .de-toolbar { padding:12px 14px; }
            #de-property, .de-select { min-width:0; width:100%; }
            .de-field { width:100%; }
            #de-source-note { margin-left:0 !important; }
            .de-col { min-width:0; flex-basis:100%; }
          }
        </style>
        <div class="dash-head">
          <h1>AI Revenue Manager</h1>
          <p>The revenue-management cockpit — pick a property for its P(book) triage calendar, pacing, forecast, and financials. Accept stages a change; nothing reaches PriceLabs until you push.</p>
        </div>
        <div class="pf-section de-toolbar">
          <div class="de-field">
            <label for="de-property">Property</label>
            <select id="de-property" class="de-select">
              <option value="">— Select a property —</option>
            </select>
          </div>
          <div class="de-field">
            <label for="de-caldays">Calendar window</label>
            <select id="de-caldays" class="de-select">
              <option value="30">Next 30 days</option>
              <option value="60">Next 60 days</option>
              <option value="90">Next 90 days</option>
              <option value="120" selected>Next 120 days</option>
            </select>
          </div>
          <span id="de-source-note" style="margin-left:auto; font-size:11px; color:var(--text-muted);"></span>
          <button class="btn btn-secondary btn-sm" id="de-queue-prev" title="Previous in the ranked queue">‹ prev</button>
          <button class="btn btn-secondary btn-sm" id="de-queue-next" title="Next in the ranked queue">next ›</button>
          <button class="btn btn-secondary btn-sm" id="de-back-health" title="Back to the Priority Queue">← Priority Queue</button>
        </div>

        <div id="de-tabs"></div>
        <div id="de-content">
          ${this.placeholder('Select a property to explore its raw data across AirROI, PriceLabs, and Hostaway.')}
        </div>

        ${PageFooter.render([
          { abbr: 'AirROI',   full: 'AirROI Market Comps',  note: 'comparable-listing revenue, ADR, and occupancy percentiles for the property' },
          { abbr: 'PriceLabs',full: 'PriceLabs Pricing',    note: 'occupancy / ADR / RevPAR / MPI across forward windows + base & recommended price' },
          { abbr: 'Percentile', full: 'Percentile (pXX)',   note: 'where a value sits in the comparable distribution — p50 is the median' },
        ])}
      </div>`;

    await this.loadListings();
    this.bindEvents();
  }

  private async loadListings(): Promise<void> {
    try {
      const res = await fetch('/api/listings');
      this.listings = res.ok ? await res.json() : [];
      this.listings.sort((a, b) => (a.short_name || a.name || '').localeCompare(b.short_name || b.name || ''));
      const sel = document.getElementById('de-property') as HTMLSelectElement;
      if (sel) sel.innerHTML = '<option value="">— Select a property —</option>' +
        this.listings.map(l => `<option value="${l.id}">${Formatters.escapeHtml(l.short_name || l.name || l.id)}</option>`).join('');

      // Deep-link from Triage ("Full workup" / fix buttons): preselect the
      // listing + tab handed over via sessionStorage, then consume the keys.
      const preselect = sessionStorage.getItem('workup_preselect');
      if (preselect) {
        sessionStorage.removeItem('workup_preselect');
        const tab = sessionStorage.getItem('workup_tab');
        sessionStorage.removeItem('workup_tab');
        if (this.listings.some(l => String(l.id) === String(preselect))) {
          this.selectedId = String(preselect);
          if (sel) sel.value = this.selectedId;
          if (tab) this.setTab(tab);
          this.pctInit = false;
          this.propensity = null;
          this.loadProperty();
        }
      }
    } catch (err) { console.error('Failed to load listings:', err); }
  }

  private bindEvents(): void {
    document.getElementById('de-back-health')?.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'triage' }));
    });
    document.getElementById('de-property')?.addEventListener('change', e => {
      this.selectedId = (e.target as HTMLSelectElement).value;
      this.pctInit = false; // new property → adopt its stored comp tier
      this.propensity = null;
      this.whyItem = null;
      this.whyTickets = null;
      this.analysis = null;
      if (this.selectedId) this.loadProperty();
    });
    document.getElementById('de-caldays')?.addEventListener('change', e => {
      this.calDays = parseInt((e.target as HTMLSelectElement).value, 10);
      if (this.selectedId) this.loadProperty();
    });
    document.getElementById('de-tabs')?.addEventListener('click', e => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-tab]');
      if (!btn) return;
      this.tab = btn.dataset.tab as Tab;
      this.renderTabs();
      this.renderContent();
    });
    // Analysis controls (percentile / year / goal pencil) — delegated on content.
    document.getElementById('de-content')?.addEventListener('click', e => {
      const t = e.target as HTMLElement;
      const pctBtn = t.closest<HTMLElement>('[data-pct]');
      if (pctBtn) {
        this.marketPct = parseInt(pctBtn.dataset.pct!, 10);
        this.pctInit = true;
        // Persist this property's comp tier so it's the default everywhere.
        fetch('/api/listings/comp-percentile', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: this.selectedId, percentile: this.marketPct }) }).catch(() => {});
        this.loadAnalysis();
        return;
      }
      const yBtn = t.closest<HTMLElement>('[data-ayear]');
      if (yBtn) { this.aYear = parseInt(yBtn.dataset.ayear!, 10); this.loadAnalysis(); return; }
      const goalBtn = t.closest<HTMLElement>('[data-goal-edit]');
      if (goalBtn) { this.editGoal(goalBtn.dataset.goalEdit!, Number(goalBtn.dataset.goalCur) || 0); return; }
      // AI RM triage calendar
      const acceptBtn = t.closest<HTMLElement>('[data-airm-accept]');
      if (acceptBtn) { const n = this.airmNight(acceptBtn.dataset.airmAccept!); if (n) this.airmStage(n); return; }
      const unstageBtn = t.closest<HTMLElement>('[data-airm-unstage]');
      if (unstageBtn) { const n = this.airmNight(unstageBtn.dataset.airmUnstage!); if (n) this.airmStage(n, true); return; }
      const pushBtn = t.closest<HTMLElement>('[data-airm-push]');
      if (pushBtn) { this.airmPush(pushBtn.dataset.airmPush === 'live'); return; }
      const snoozeBtn = t.closest<HTMLElement>('[data-airm-snooze]');
      if (snoozeBtn) { this.airmSuppress(snoozeBtn.dataset.airmSnooze!, 'snooze'); return; }
      const dismissBtn = t.closest<HTMLElement>('[data-airm-dismiss]');
      if (dismissBtn) { this.airmSuppress(dismissBtn.dataset.airmDismiss!, 'dismiss'); return; }
      const acceptAllBtn = t.closest<HTMLElement>('[data-airm-acceptall]');
      if (acceptAllBtn) { this.airmAcceptAll(); return; }
      const whyAct = t.closest<HTMLElement>('[data-why-act]');
      if (whyAct) { this.whyAction(whyAct.dataset.whyKey!, whyAct.dataset.whyAct!); return; }
      const whyFix = t.closest<HTMLElement>('[data-why-fix]');
      if (whyFix) { this.setTab(whyFix.dataset.whyFix!); this.renderTabs(); this.renderContent(); return; }
      // Review tickets (Why tab): status moves + the inline manual opener.
      const rvtBtn = t.closest<HTMLElement>('[data-rvt-status]');
      if (rvtBtn) { this.ticketAction(rvtBtn.dataset.rvtId!, rvtBtn.dataset.rvtStatus!); return; }
      if (t.closest('#why-ticket-add')) {
        const input = document.getElementById('why-ticket-input') as HTMLInputElement | null;
        this.ticketCreate(input?.value || '');
        return;
      }
      if (t.closest('#why-mtr')) {
        const lid = this.selectedId;
        fetch('/api/mtr-tags').then(r => r.ok ? r.json() : {}).then(async (tags) => {
          const tagged = !tags[String(lid)];
          await fetch('/api/mtr-tags', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ listing_id: lid, tagged }) });
          const btn = document.getElementById('why-mtr');
          if (btn) btn.textContent = tagged ? 'MTR ✓' : 'MTR…';
        }).catch(() => {});
        return;
      }
      const srcBtn = t.closest<HTMLElement>('[data-de-source]');
      if (srcBtn) { this.dataSource = srcBtn.dataset.deSource as DataSource; this.renderContent(); return; }
      const adjBtn = t.closest<HTMLElement>('[data-airm-adjust]');
      if (adjBtn) { const n = this.airmNight(adjBtn.dataset.airmAdjust!); if (n) this.airmManualAdjust(n); return; }
      const qPrev = t.closest('#de-queue-prev'); if (qPrev) { this.stepQueue(-1); return; }
      const qNext = t.closest('#de-queue-next'); if (qNext) { this.stepQueue(1); return; }
      const cellEl = t.closest<HTMLElement>('[data-airm-date]');
      if (cellEl) {
        this.airmSelected = cellEl.dataset.airmDate!;
        this.airmExplain = null;
        this.renderContent();
        const n = this.airmNight(this.airmSelected);
        if (n) this.airmLoadExplain(n);
        return;
      }
    });
  }

  private async editGoal(monthKey: string, current: number): Promise<void> {
    const input = window.prompt(`Set revenue goal for ${monthKey} (blank to clear and use the auto-projection):`, current ? String(current) : '');
    if (input === null) return;
    const goal = input.trim() === '' ? 0 : Number(input.replace(/[^0-9.]/g, ''));
    try {
      const res = await fetch(`/api/property/${this.selectedId}/goal-override`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month_key: monthKey, goal }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this.loadAnalysis();
    } catch (err) { console.error('goal save failed', err); alert('Could not save the goal. Try again.'); }
  }

  private async loadProperty(): Promise<void> {
    const content = document.getElementById('de-content');
    if (content) content.innerHTML = `<div class="pf-section" style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div>`;
    this.loading = true;
    try {
      const res = await fetch(`/api/property/${this.selectedId}/explorer?days=${this.calDays}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this.data = await res.json();
    } catch (err) {
      console.error('Explorer load failed:', err);
      if (content) content.innerHTML = '<div class="pf-section" style="padding:40px; text-align:center; color:var(--danger);">Failed to load property data.</div>';
      this.loading = false;
      return;
    }
    this.loading = false;
    const note = document.getElementById('de-source-note');
    if (note) {
      const have = [this.data.airroi && 'AirROI', this.data.pricelabs && 'PriceLabs', 'Hostaway'].filter(Boolean).join(' · ');
      note.textContent = `Sources: ${have}`;
    }
    document.getElementById('de-tabs')!.style.display = 'flex';
    this.renderTabs();
    this.renderContent();
    this.loadAnalysis();
  }

  private async loadAnalysis(): Promise<void> {
    if (!this.selectedId) return;
    // On first load for a property, let the server use its stored comp tier; then
    // adopt that value so the toggle reflects it. After that, send the chosen tier.
    const pctParam = this.pctInit ? `&percentile=${this.marketPct}` : '';
    try {
      const res = await fetch(`/api/property/${this.selectedId}/analysis?year=${this.aYear}&month=${this.aMonth}${pctParam}`);
      this.analysis = res.ok ? await res.json() : null;
    } catch (_) { this.analysis = null; }
    if (this.analysis?.meta?.percentile) { this.marketPct = this.analysis.meta.percentile; this.pctInit = true; }
    if (ANALYSIS_TABS.includes(this.tab)) this.renderContent();
    this.renderTabs();
  }

  private async loadPropensity(): Promise<void> {
    if (!this.selectedId) return;
    try {
      const res = await fetch(`/api/airm/calendar/${this.selectedId}?days=${Math.min(180, this.calDays)}`);
      this.propensity = res.ok ? await res.json() : { error: true, nights: [] };
    } catch (_) { this.propensity = { error: true, nights: [] }; }
    if (this.tab === 'propensity') this.renderContent();
  }

  // ── AI RM triage calendar interactions ──────────────────────
  private airmSelected: string | null = null;     // selected open date
  private airmExplain: any = null;                // {text, source} for the selection
  private airmPushResult: any = null;             // last push response

  private airmNight(date: string): any {
    return (this.propensity?.nights || []).find((n: any) => n.date === date) || null;
  }

  private async airmLoadExplain(n: any): Promise<void> {
    this.airmExplain = null;
    try {
      const res = await fetch('/api/airm/explain', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_id: this.selectedId, date: n.date, p: (n.probability || 0) / 100,
          lead: n.days_out, lo: n.band?.[0], hi: n.band?.[1],
          direction: n.direction, bucket: n.bucket, price: n.price,
          target_price: n.suggestion?.target_price, change_pct: n.suggestion?.change_pct,
        }),
      });
      this.airmExplain = res.ok ? await res.json() : { text: 'Explanation unavailable.', source: 'template' };
    } catch (_) { this.airmExplain = { text: 'Explanation unavailable.', source: 'template' }; }
    if (this.tab === 'propensity') this.renderContent();
  }

  private async airmStage(n: any, unstage = false): Promise<void> {
    await fetch('/api/airm/stage', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(unstage
        ? { listing_id: this.selectedId, date: n.date, action: 'unstage' }
        : {
            listing_id: this.selectedId, date: n.date,
            target_price: n.suggestion?.target_price, current_price: n.price,
            change_pct: n.suggestion?.change_pct, p: n.probability, lever: n.suggestion?.lever,
          }),
    });
    await this.loadPropensity();
  }

  private async airmSuppress(date: string, action: 'snooze' | 'dismiss' | 'unsuppress'): Promise<void> {
    await fetch('/api/airm/stage', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ listing_id: this.selectedId, date, action }),
    });
    await this.loadPropensity();
  }

  private async airmAcceptAll(): Promise<void> {
    const targets = (this.propensity?.nights || []).filter((n: any) =>
      n.status === 'open' && !n.staged && !n.suppressed && n.suggestion?.target_price &&
      ['moderate', 'severe', 'critical'].includes(n.bucket));
    for (const n of targets) {
      await fetch('/api/airm/stage', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_id: this.selectedId, date: n.date,
          target_price: n.suggestion.target_price, current_price: n.price,
          change_pct: n.suggestion.change_pct, p: n.probability, lever: n.suggestion.lever,
        }),
      });
    }
    await this.loadPropensity();
  }

  private async airmPush(live: boolean): Promise<void> {
    if (live && !window.confirm(`Push ${this.propensity?.staged_count || 0} staged price override(s) LIVE to PriceLabs for this listing?`)) return;
    try {
      const res = await fetch('/api/airm/push', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listing_id: this.selectedId, dry_run: !live }),
      });
      this.airmPushResult = await res.json();
    } catch (_) { this.airmPushResult = { error: true }; }
    await this.loadPropensity();
  }

  // ── Why tab — the unified flag panel (absorbs the killed Health drawer) ──
  private whyItem: any = null;
  // Per-property review tickets — "where we see actions taken to improve the
  // guest experience" (open work + the closed proof), sourced from sub-8 reviews.
  private whyTickets: any[] | null = null;
  private whyTicketsAsof: any = null;
  private async loadWhy(): Promise<void> {
    if (!this.selectedId) return;
    try {
      const [all, issues] = await Promise.all([
        fetch('/api/triage').then(r => r.ok ? r.json() : []),
        fetch('/api/reviews/issues?listing_id=' + encodeURIComponent(this.selectedId))
          .then(r => r.ok ? r.json() : { analysis: null, tickets: [] }),
      ]);
      this.whyItem = (Array.isArray(all) ? all : []).find((x: any) => String(x.listing?.id) === String(this.selectedId)) || { none: true };
      this.whyTickets = Array.isArray(issues?.tickets) ? issues.tickets : [];
      this.whyTicketsAsof = issues?.analysis?.asof || null;
    } catch (_) {
      this.whyItem = { none: true };
      this.whyTickets = this.whyTickets || [];
    }
    if (this.tab === 'why') this.renderContent();
  }

  private tabWhy(): string {
    const it = this.whyItem;
    // Flags may be absent for a healthy listing — review tickets still render,
    // so the tab never dead-ends on 'no flag data'.
    if (!it || it.none || !it.buckets) {
      return this.empty('No flag data for this listing.') + this.whyTicketsSection();
    }
    const card = (f: any) => {
      const sevDot = f.severity === 'high' ? 'var(--danger)' : f.severity === 'warn' ? 'var(--warning)' : 'var(--info)';
      const target = /price|gap|pace|pickup|mpi|stly/i.test(f.key || '') ? 'propensity' : /review/i.test(f.key || '') ? 'reviews' : 'pricelabs';
      return `
        <div style="border:1px solid var(--border); border-radius:8px; padding:11px 14px; margin-bottom:8px;">
          <div style="display:flex; align-items:baseline; gap:8px;">
            <span style="width:8px; height:8px; border-radius:50%; background:${sevDot}; flex-shrink:0; align-self:center;"></span>
            <span style="font-size:13px; font-weight:600;">${Formatters.escapeHtml(f.title)}</span>
            <span style="flex:1;"></span>
            <button class="btn btn-secondary btn-sm" data-why-fix="${target}" style="font-size:10px; padding:2px 9px;">${Formatters.escapeHtml(f.fix?.label || 'Fix')}</button>
            <button class="btn btn-secondary btn-sm" data-why-act="done" data-why-key="${f.key}" style="font-size:10px; padding:2px 9px;">Done</button>
            <button class="btn btn-secondary btn-sm" data-why-act="snooze" data-why-key="${f.key}" style="font-size:10px; padding:2px 9px;">Snooze 7d</button>
            <button class="btn btn-secondary btn-sm" data-why-act="dismiss" data-why-key="${f.key}" style="font-size:10px; padding:2px 9px;">Dismiss</button>
          </div>
          <div style="font-size:12px; color:var(--text-muted); margin-top:5px; line-height:1.5;">${Formatters.escapeHtml(f.why)}</div>
        </div>`;
    };
    const perf = it.buckets.performance?.flags || [];
    const qual = it.buckets.quality?.flags || [];
    const sup = it.buckets.suppressed || [];
    const section = (t: string, arr: any[]) => arr.length
      ? `<div style="font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; margin:14px 0 8px;">${t}</div>${arr.map(card).join('')}`
      : '';
    const supRows = sup.length
      ? `<div style="font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; margin:14px 0 8px;">Handled</div>` +
        sup.map((f: any) => `
          <div style="display:flex; align-items:center; gap:8px; padding:6px 0; border-bottom:1px solid var(--border-soft); font-size:12px; color:var(--text-muted);">
            <span>${Formatters.escapeHtml(f.title)}</span>
            <span style="font-size:10px;">(${f.suppression?.action}${f.suppression?.until ? ' until ' + String(f.suppression.until).slice(0, 10) : ''})</span>
            <span style="flex:1;"></span>
            <button class="btn btn-secondary btn-sm" data-why-act="undo" data-why-key="${f.key}" style="font-size:10px; padding:2px 9px;">Undo</button>
          </div>`).join('')
      : '';
    return `
      <div style="display:flex; align-items:center; gap:10px; margin-bottom:4px;">
        <span style="font-size:12px; color:var(--text-muted);">Every flag is a reason revenue is off — fix routes stay inside this window; Done / Snooze / Dismiss feed the audit trail.</span>
        <span style="flex:1;"></span>
        <button class="btn btn-secondary btn-sm" id="why-mtr" style="font-size:10px; padding:2px 9px;" title="Midterm rentals are excluded from booking-recency expectations">MTR…</button>
      </div>
      ${section('Performance — fix the price', perf)}
      ${section('Quality — fix the asset', qual)}
      ${perf.length + qual.length === 0 ? this.empty('No open flags — pace or pricing put this listing in the queue.') : ''}
      ${this.whyTicketsSection()}
      ${supRows}`;
  }

  // Guest experience — per-property review tickets. Open work first (Start /
  // Done / Dismiss feed the ticket log), then the newest closed ones as the
  // "actions taken" proof, then an inline opener for manual tickets.
  private whyTicketsSection(): string {
    const head = `<div style="font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; margin:14px 0 8px;">Guest experience — review tickets</div>`;
    const provLine = `<div style="font-size:11px; color:var(--text-faint, #9ca3af); margin-bottom:8px;">from sub-8 guest reviews, last 90 days · ${this.whyTicketsAsof ? `analysis as of ${Formatters.escapeHtml(String(this.whyTicketsAsof))}` : 'no analysis run yet'}</div>`;
    const tickets = this.whyTickets || [];
    const active = tickets.filter((t: any) => ['open', 'in_progress'].includes(t.status));
    const closed = tickets.filter((t: any) => ['done', 'dismissed'].includes(t.status))
      .sort((a: any, b: any) => String(b.updated_at || b.created_at || '').localeCompare(String(a.updated_at || a.created_at || '')))
      .slice(0, 5);
    const sevDot = (s: string) => s === 'high' ? 'var(--danger)' : s === 'medium' ? 'var(--warning)' : 'var(--info)';
    const cards = active.map((t: any) => {
      const ts = new Date(t.created_at || '').getTime();
      const age = Number.isFinite(ts) ? Math.max(0, Math.floor((Date.now() - ts) / 86400000)) : null;
      const last = Array.isArray(t.log) && t.log.length ? t.log[t.log.length - 1] : null;
      const idAttr = Formatters.escapeHtml(String(t.id));
      return `
        <div style="border:1px solid var(--border); border-radius:8px; padding:11px 14px; margin-bottom:8px;">
          <div style="display:flex; align-items:baseline; gap:8px; flex-wrap:wrap;">
            <span style="width:8px; height:8px; border-radius:50%; background:${sevDot(t.severity)}; flex-shrink:0; align-self:center;"></span>
            <span style="font-size:13px; font-weight:600;">${Formatters.escapeHtml(t.theme || '')}</span>
            <span style="font-size:9px; padding:1px 6px; border-radius:999px; background:${t.source === 'manual' ? 'var(--bg-subtle)' : '#eaf2ff'}; color:${t.source === 'manual' ? 'var(--text-muted)' : '#1d4ed8'};">${t.source === 'manual' ? 'manual' : 'auto'}</span>
            ${t.status === 'in_progress' ? `<span style="font-size:9px; padding:1px 6px; border-radius:999px; background:var(--warning-soft); color:var(--warning-text);">in progress</span>` : ''}
            ${age != null ? `<span class="mono num" style="font-size:11px; color:var(--text-muted);">open ${age}d</span>` : ''}
            <span style="flex:1;"></span>
            ${t.status === 'open' ? `<button class="btn btn-secondary btn-sm" data-rvt-id="${idAttr}" data-rvt-status="in_progress" style="font-size:10px; padding:2px 9px;">Start</button>` : ''}
            <button class="btn btn-secondary btn-sm" data-rvt-id="${idAttr}" data-rvt-status="done" style="font-size:10px; padding:2px 9px;">Done</button>
            <button class="btn btn-secondary btn-sm" data-rvt-id="${idAttr}" data-rvt-status="dismissed" style="font-size:10px; padding:2px 9px;">Dismiss</button>
          </div>
          ${t.suggested_action ? `<div style="font-size:12px; color:var(--text-muted); margin-top:5px; line-height:1.5;">${Formatters.escapeHtml(t.suggested_action)}</div>` : ''}
          ${last ? `<div style="font-size:11px; color:var(--text-faint, #9ca3af); margin-top:4px;">${Formatters.escapeHtml(String(last.at || '').slice(0, 10))} · ${Formatters.escapeHtml(String(last.status || ''))}${last.note ? ' — ' + Formatters.escapeHtml(String(last.note)) : ''}</div>` : ''}
        </div>`;
    }).join('');
    // Closed tickets are the owner-facing proof that actions were taken.
    const closedRows = closed.map((t: any) => `
      <div style="display:flex; align-items:center; gap:8px; padding:5px 0; border-bottom:1px solid var(--border-soft); font-size:12px; color:var(--text-muted);">
        <span>${t.status === 'done' ? '✓' : '✕'}</span>
        <span style="flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${Formatters.escapeHtml(t.theme || '')}</span>
        <span class="mono" style="font-size:11px;">${t.status} ${Formatters.escapeHtml(String(t.updated_at || t.created_at || '').slice(0, 10))}</span>
      </div>`).join('');
    const emptyMsg = !active.length && !closed.length
      ? this.empty(this.whyTicketsAsof
        ? 'Analysis ran — no review tickets touch this listing.'
        : 'No review tickets yet — run the analysis on the Reviews page, or open one below.')
      : '';
    const addForm = `
      <div style="display:flex; gap:8px; margin-top:10px;">
        <input id="why-ticket-input" type="text" maxlength="200" placeholder="Open a ticket for this property — what needs fixing?"
          style="flex:1; min-width:0; padding:7px 10px; border:1px solid var(--border); border-radius:8px; font-size:12px; background:#fff; color:var(--text); font-family:inherit;">
        <button class="btn btn-secondary btn-sm" id="why-ticket-add" style="font-size:11px; white-space:nowrap;">+ Open ticket</button>
      </div>`;
    return head + provLine + cards + emptyMsg
      + (closedRows ? `<div style="font-size:11px; color:var(--text-muted); margin:10px 0 2px;">Actions taken — ${closed.length} newest closed</div>${closedRows}` : '')
      + addForm;
  }

  private async ticketAction(id: string, status: string): Promise<void> {
    let note = '';
    if (status === 'done') {
      const v = window.prompt('What was done? (logged on the ticket — the owner-facing proof of action)', '');
      if (v === null) return;
      note = v.trim();
    }
    try {
      await fetch('/api/reviews/issues', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status, note }),
      });
    } catch (_) { /* reload shows the truth either way */ }
    this.whyTickets = null;
    this.loadWhy();
  }

  private async ticketCreate(theme: string): Promise<void> {
    const v = (theme || '').trim();
    if (!v || !this.selectedId) return;
    try {
      await fetch('/api/reviews/issues', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ theme: v.slice(0, 200), listing_id: this.selectedId }),
      });
    } catch (_) { /* reload shows the truth either way */ }
    this.whyTickets = null;
    this.loadWhy();
  }

  private async whyAction(flagKey: string, action: string): Promise<void> {
    await fetch('/api/triage/flag-actions', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ listing_id: this.selectedId, flag_key: flagKey, action }),
    });
    this.whyItem = null;
    this.loadWhy();
  }

  // ── Queue prev/next — triage as a flow (spec 02 Block E) ──
  private queueIds(): string[] {
    try { return JSON.parse(sessionStorage.getItem('workup_queue') || '[]'); } catch (_) { return []; }
  }
  private stepQueue(dir: 1 | -1): void {
    const ids = this.queueIds();
    if (!ids.length) return;
    const i = ids.indexOf(String(this.selectedId));
    const next = ids[(i < 0 ? 0 : (i + dir + ids.length) % ids.length)];
    const sel = document.getElementById('de-property') as HTMLSelectElement | null;
    if (sel && next) { sel.value = next; sel.dispatchEvent(new Event('change')); }
  }

  // "I adjusted it" — non-price levers reach the Impact ledger too.
  private async airmManualAdjust(n: any): Promise<void> {
    await fetch('/api/airm/stage', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ listing_id: this.selectedId, date: n.date, action: 'manual_adjust', p: n.probability, lever: n.suggestion?.lever || 'other' }),
    });
    await this.loadPropensity();
  }

  private tabPropensity(): string {
    const p = this.propensity;
    if (!p || p.error) return `<div style="color:var(--danger); font-size:13px;">Failed to load the P(book) calendar.</div>`;
    const BUCKET: Record<string, { bg: string; fg: string; label: string }> = {
      healthy:  { bg: '#fff',                fg: 'var(--text)',        label: 'healthy' },
      mild:     { bg: 'var(--warning-soft)', fg: 'var(--warning-text)', label: 'mild' },
      moderate: { bg: '#ffedd5',             fg: '#9a3412',            label: 'moderate' },
      severe:   { bg: 'var(--danger-soft)',  fg: 'var(--danger-text)',  label: 'severe' },
      critical: { bg: '#fee2e2',             fg: '#7f1d1d',            label: 'critical' },
    };
    const counts = p.counts || {};
    const chip = (k: string, n: number) => n
      ? `<span style="font-size:11px; padding:2px 8px; border-radius:999px; background:${BUCKET[k]?.bg || 'var(--bg-subtle)'}; color:${BUCKET[k]?.fg || 'var(--text-muted)'}; border:1px solid var(--border);" class="mono">${n} ${k}</span>`
      : '';
    const scoreColor = p.score <= 10 ? 'var(--success)' : p.score <= 30 ? 'var(--warning)' : 'var(--danger)';

    const header = `
      <div style="display:flex; align-items:center; gap:18px; flex-wrap:wrap; margin-bottom:6px;">
        <div><div style="font-size:10px; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Health score</div>
          <div class="mono num" style="font-size:24px; font-weight:500; color:${scoreColor};">${p.score}</div></div>
        <div><div style="font-size:10px; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">In band</div>
          <div class="mono num" style="font-size:24px; font-weight:500;">${p.healthy_pct}%</div></div>
        <div style="display:flex; gap:6px; flex-wrap:wrap;">${['mild','moderate','severe','critical'].map(k => chip(k, counts[k] || 0)).join('')}</div>
        <span style="flex:1;"></span>
        ${p.staged_count ? `
          <span style="font-size:12px; color:var(--text-muted);" class="mono">${p.staged_count} staged</span>
          <button class="btn btn-secondary btn-sm" data-airm-push="dry">Push all · dry run</button>
          <button class="btn btn-primary btn-sm" data-airm-push="live">Push live to PriceLabs</button>` : ''}
      </div>
      <div style="font-size:11px; color:var(--text-faint, var(--text-muted)); margin-bottom:12px;">
        Score 0 = every open date inside its healthy band (P in [0.50,0.80] for &gt;21d / [0.60,0.85] for 14–21d / [0.70,0.95] for &lt;14d) · weights mild 1 / moderate 4 / severe 9 / critical 16 · booked dates excluded.
        Click a date for the why + the suggested move. Accept stages a change; nothing reaches PriceLabs until you push.
      </div>
      ${this.airmPushResult ? `<div style="font-size:12px; border:1px solid var(--border); border-radius:8px; padding:8px 12px; margin-bottom:12px; background:var(--bg-subtle);" class="mono">${this.airmPushResult.dry_run ? `DRY RUN — would push ${this.airmPushResult.would_push} override(s): ` : `PUSHED ${this.airmPushResult.pushed} override(s): `}${(this.airmPushResult.overrides || []).map((o: any) => `${o.date}→$${o.price}`).join(' · ') || '(none staged with a target price)'}</div>` : ''}`;

    const nights = p.nights || [];
    if (!nights.length) return header + this.empty('No nights in range.');

    // 7-col grid aligned to weekday, with month labels at boundaries.
    const firstDow = new Date(nights[0].date + 'T00:00:00').getDay();
    let cells = Array.from({ length: firstDow }, () => '<div></div>');
    let lastMonth = '';
    for (const n of nights) {
      const d = new Date(n.date + 'T00:00:00');
      const mon = d.toLocaleDateString('en-US', { month: 'short' });
      const monthTag = mon !== lastMonth ? `<div style="font-size:9px; color:var(--text-muted); text-transform:uppercase;">${mon}</div>` : '';
      lastMonth = mon;
      if (n.status === 'booked') {
        cells.push(`<div style="border:1px solid var(--border-soft); border-radius:6px; padding:5px 6px; background:var(--bg-subtle); color:var(--text-muted); min-height:54px;">${monthTag}<div class="mono" style="font-size:11px;">${d.getDate()}</div><div style="font-size:10px;">booked ✓</div></div>`);
        continue;
      }
      if (n.status === 'blocked') {
        cells.push(`<div style="border:1px dashed var(--border); border-radius:6px; padding:5px 6px; color:var(--text-faint, #9ca3af); min-height:54px;">${monthTag}<div class="mono" style="font-size:11px;">${d.getDate()}</div><div style="font-size:10px;">blocked</div></div>`);
        continue;
      }
      const b = BUCKET[n.bucket] || BUCKET.healthy;
      const sel = this.airmSelected === n.date;
      const sugg = n.suggestion?.change_pct != null
        ? `<div class="mono" style="font-size:10px; font-weight:600;">${n.suggestion.change_pct > 0 ? '+' : ''}${n.suggestion.change_pct}%</div>`
        : (n.suggestion ? `<div style="font-size:9px;">⚡</div>` : '');
      cells.push(`<div data-airm-date="${n.date}" style="border:1px solid ${sel ? 'var(--text)' : 'var(--border)'}; ${sel ? 'box-shadow: inset 0 0 0 1px var(--text);' : ''} border-radius:6px; padding:5px 6px; background:${b.bg}; color:${b.fg}; cursor:pointer; min-height:54px; ${n.suppressed ? 'opacity:0.45;' : ''}">
        ${monthTag}
        <div style="display:flex; justify-content:space-between; align-items:baseline;"><span class="mono" style="font-size:11px;">${d.getDate()}</span><span class="mono num" style="font-size:11px; font-weight:600;">${n.probability}%</span></div>
        <div class="mono" style="font-size:10px; opacity:0.85;">${n.price ? '$' + Math.round(n.price) : '—'}</div>
        ${n.staged ? '<div style="font-size:9px; font-weight:700;">STAGED</div>' : n.suppressed ? `<div style="font-size:9px;">${n.suppressed.action === 'snooze' ? 'snoozed' : 'dismissed'}</div>` : sugg}
      </div>`);
    }
    const grid = `
      <div style="display:grid; grid-template-columns:repeat(7,1fr); gap:4px; font-size:10px; color:var(--text-muted); margin-bottom:4px;">${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => `<div style="text-align:center;">${d}</div>`).join('')}</div>
      <div style="display:grid; grid-template-columns:repeat(7,1fr); gap:4px;">${cells.join('')}</div>`;

    // Detail panel for the selected date.
    let detail = '';
    const selN = this.airmSelected ? this.airmNight(this.airmSelected) : null;
    if (selN && selN.status === 'open') {
      const b = BUCKET[selN.bucket] || BUCKET.healthy;
      const exp = this.airmExplain;
      detail = `
        <div style="border:1px solid var(--border); border-radius:8px; padding:14px 16px; margin-top:14px;">
          <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap;">
            <span style="font-weight:600;">${new Date(selN.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
            <span style="font-size:11px; padding:2px 8px; border-radius:999px; background:${b.bg}; color:${b.fg}; border:1px solid var(--border);">${selN.bucket} ${selN.direction !== 'healthy' ? '· ' + selN.direction : ''}</span>
            <span class="mono num" style="font-size:12px; color:var(--text-muted);">P=${(selN.probability / 100).toFixed(2)} · band [${selN.band[0].toFixed(2)}, ${selN.band[1].toFixed(2)}] · T-${selN.days_out}d</span>
            <span style="flex:1;"></span>
            ${selN.staged
              ? `<button class="btn btn-secondary btn-sm" data-airm-unstage="${selN.date}">Unstage</button>`
              : (selN.suggestion?.target_price ? `<button class="btn btn-primary btn-sm" data-airm-accept="${selN.date}">Accept · ${selN.suggestion.change_pct > 0 ? '+' : ''}${selN.suggestion.change_pct}% to $${selN.suggestion.target_price}</button>` : '')}
          </div>
          <div style="display:flex; gap:24px; flex-wrap:wrap; margin-top:10px; font-size:12px;">
            <div><span style="color:var(--text-muted);">Current price:</span> <span class="mono num">${selN.price ? '$' + Math.round(selN.price) : 'unknown'}</span> <span style="color:var(--text-faint, #9ca3af); font-size:10px;">${selN.price_source === 'calendar' ? '' : selN.price_source === 'adr_90d' ? '(est. from trailing-90d ADR)' : selN.price_source === 'adr_365d' ? '(est. from trailing-365d ADR)' : selN.price_source === 'market_adr' ? '(est. from market ADR)' : ''}</span></div>
            ${selN.allin ? `<div><span style="color:var(--text-muted);">Est. all-in guest price:</span> <span class="mono num">$${selN.allin}</span> <span style="color:var(--text-faint, #9ca3af); font-size:10px;">nightly + $${p.cleaning_per_night}/night cleaning (avg ${p.avg_los}-night stay) — before channel fees</span></div>` : ''}
            ${selN.min_stay ? `<div><span style="color:var(--text-muted);">Min stay:</span> <span class="mono num">${selN.min_stay}</span></div>` : ''}
            ${selN.suggestion ? `<div><span style="color:var(--text-muted);">Suggested lever:</span> ${selN.suggestion.label} <span style="color:var(--text-faint, #9ca3af);">(${selN.suggestion.system})</span></div>` : '<div style="color:var(--text-muted);">In band — no action.</div>'}
          </div>
          <div style="margin-top:10px; font-size:12px; line-height:1.55; color:#374151;">
            ${exp ? `${exp.text} <span style="font-size:9px; padding:1px 6px; border-radius:999px; background:${exp.source === 'claude' ? '#eaf2ff' : 'var(--bg-subtle)'}; color:${exp.source === 'claude' ? '#1d4ed8' : 'var(--text-muted)'};">${exp.source === 'claude' ? '🧠 claude' : 'template'}</span>` : '<span style="color:var(--text-faint, #9ca3af);">Loading explanation…</span>'}
          </div>
        </div>`;
    }
    // At-risk rail (wchdash functionality): worst flagged dates with the
    // action set — Accept / Snooze 7d / Dismiss — plus Accept-all.
    const RANK: Record<string, number> = { critical: 0, severe: 1, moderate: 2 };
    const atRisk = nights
      .filter((n: any) => n.status === 'open' && !n.suppressed && RANK[n.bucket] != null)
      .sort((a: any, b: any) => (RANK[a.bucket] - RANK[b.bucket]) || (b.deviation - a.deviation))
      .slice(0, 10);
    const acceptable = atRisk.filter((n: any) => n.suggestion?.target_price && !n.staged).length;
    const railRows = atRisk.map((n: any) => {
      const b = BUCKET[n.bucket];
      const d2 = new Date(n.date + 'T00:00:00');
      return `<div style="padding:10px 12px; border-bottom:1px solid var(--border-soft);">
        <div style="display:flex; align-items:baseline; gap:8px;">
          <span class="mono" style="font-size:12px; font-weight:600;">${d2.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          <span style="font-size:10px; padding:1px 7px; border-radius:999px; background:${b.bg}; color:${b.fg}; border:1px solid var(--border);">${n.bucket}</span>
          <span class="mono num" style="font-size:11px; color:var(--text-muted);">P=${(n.probability / 100).toFixed(2)} · T-${n.days_out}d</span>
          ${n.rule ? `<span style="font-size:9px; padding:1px 6px; border-radius:999px; background:var(--info-soft); color:var(--info-text);" title="published $${n.rule.published} vs PriceLabs $${n.rule.recommended}">vs PL ${n.rule.direction}</span>` : ''}
        </div>
        <div class="mono num" style="font-size:11px; margin-top:3px; color:var(--text-muted);">
          ${n.suggestion?.target_price ? `$${Math.round(n.price)} → $${n.suggestion.target_price} (${n.suggestion.change_pct > 0 ? '+' : ''}${n.suggestion.change_pct}%)` : (n.suggestion?.label || 'no price move')}
        </div>
        <div style="display:flex; gap:6px; margin-top:6px;">
          ${n.staged
            ? `<button class="btn btn-secondary btn-sm" data-airm-unstage="${n.date}" style="font-size:10px; padding:3px 8px;">staged ✓ unstage</button>`
            : (n.suggestion?.target_price
              ? `<button class="btn btn-primary btn-sm" data-airm-accept="${n.date}" style="font-size:10px; padding:3px 8px;">Accept</button>`
              : (n.suggestion ? `<button class="btn btn-primary btn-sm" data-airm-adjust="${n.date}" style="font-size:10px; padding:3px 8px;" title="Log a non-price fix (promo, min-stay) to the Impact ledger">I adjusted it</button>` : ''))}
          <button class="btn btn-secondary btn-sm" data-airm-snooze="${n.date}" style="font-size:10px; padding:3px 8px;">Snooze 7d</button>
          <button class="btn btn-secondary btn-sm" data-airm-dismiss="${n.date}" style="font-size:10px; padding:3px 8px;">Dismiss</button>
        </div>
      </div>`;
    }).join('');
    const rail = `
      <div style="border:1px solid var(--border); border-radius:8px; background:#fff; overflow:hidden;">
        <div style="display:flex; align-items:center; gap:8px; padding:10px 12px; border-bottom:1px solid var(--border);">
          <span style="font-size:12px; font-weight:600;">At-risk dates</span>
          <span class="mono num" style="font-size:11px; color:var(--text-muted);">top ${atRisk.length}</span>
          <span style="flex:1;"></span>
          ${acceptable ? `<button class="btn btn-primary btn-sm" data-airm-acceptall="1" style="font-size:10px; padding:3px 9px;">Accept all (${acceptable})</button>` : ''}
        </div>
        ${railRows || '<div style="padding:16px 12px; font-size:12px; color:var(--text-faint, #9ca3af); text-align:center;">No at-risk dates — the book is healthy.</div>'}
      </div>`;
    const footer = p.staged_count > 0 ? `
      <div data-bottom-bar style="position:sticky; bottom:0; margin-top:14px; background:#fff; border:1px solid var(--border); border-radius:8px; padding:10px 16px; display:flex; align-items:center; gap:12px; box-shadow:0 -4px 16px rgba(0,0,0,0.05); z-index:10;">
        <span class="mono num" style="font-size:13px; font-weight:600;">${p.staged_count} staged</span>
        <span style="font-size:11px; color:var(--text-muted);">accepted changes wait here until you push — nothing reaches PriceLabs by itself</span>
        <span style="flex:1;"></span>
        <button class="btn btn-secondary btn-sm" data-airm-push="dry">Dry run</button>
        <button class="btn btn-primary btn-sm" data-airm-push="live">Push to PriceLabs</button>
      </div>` : '';
    return header + `<div style="display:grid; grid-template-columns:minmax(0,1fr) 330px; gap:14px; align-items:start;"><div>` + grid + detail + `</div>` + rail + `</div>` + footer;
  }

  private renderTabs(): void {
    const el = document.getElementById('de-tabs');
    if (!el) return;
    const tabs: { id: Tab; label: string; n?: number }[] = [
      { id: 'propensity', label: 'Calendar' },
      { id: 'why', label: 'Why' },
      { id: 'pacing', label: 'Pacing' },
      { id: 'financials', label: 'Financials' },
      { id: 'data', label: 'Data' },
    ];
    el.innerHTML = tabs.map(t => {
      const active = t.id === this.tab;
      const count = Number.isFinite(Number(t.n)) ? ` <span class="de-tab-count">${t.n}</span>` : '';
      return `<button class="de-tab${active ? ' active' : ''}" data-tab="${t.id}">${t.label}${count}</button>`;
    }).join('');
  }

  private renderContent(): void {
    const el = document.getElementById('de-content');
    if (!el || !this.data) return;
    const wrap = (inner: string) => `<div class="pf-section" style="padding:18px 20px;">${inner}</div>`;
    if (ANALYSIS_TABS.includes(this.tab) && !this.analysis) {
      el.innerHTML = wrap(`<div style="text-align:center; padding:24px;">${LoadingSpinner.render()}</div>`);
      return;
    }
    if (this.tab === 'why' && (!this.whyItem || this.whyTickets === null)) {
      el.innerHTML = wrap(`<div style="text-align:center; padding:24px;">${LoadingSpinner.render()}</div>`);
      this.loadWhy();
      return;
    }
    if (this.tab === 'propensity' && !this.propensity) {
      el.innerHTML = wrap(`<div style="text-align:center; padding:24px;">${LoadingSpinner.render()}</div>`);
      this.loadPropensity();
      return;
    }
    const divider = `<div style="border-top:1px solid var(--border); margin:24px -20px 18px;"></div>`;
    let html = '';
    switch (this.tab) {
      case 'propensity':   html = this.tabPropensity(); break;
      case 'why':          html = this.tabWhy(); break;
      case 'pacing':       html = this.tabPacing() + divider + this.mergeHead('Forecast vs actual', 'monthly goals against booked revenue') + this.tabForecast(false); break;
      case 'financials':   html = this.tabFinancials() + divider + this.mergeHead('Branded report', 'the same artifact owners receive, RM voice') + this.tabReport(); break;
      case 'data':         html = this.tabData(); break;
    }
    el.innerHTML = wrap(html);
    if (this.tab === 'financials') this.mountReportDoc();
  }

  // Heading between merged sections of a consolidated tab.
  private mergeHead(title: string, sub: string): string {
    return `<div style="margin-bottom:12px;"><span style="font-size:13px; font-weight:700;">${title}</span> <span style="font-size:11px; color:var(--text-muted);">— ${sub}</span></div>`;
  }

  // Data tab — raw feeds behind a source sub-picker (reference, not workflow).
  private tabData(): string {
    const counts: Partial<Record<DataSource, number>> = {
      reservations: this.data?.reservations?.length,
      calendar: this.data?.calendar?.length,
      reviews: this.data?.reviews?.length,
      tasks: this.data?.tasks?.length,
    };
    const labels: Record<DataSource, string> = {
      overview: 'Overview', airroi: 'AirROI', pricelabs: 'PriceLabs',
      reservations: 'Reservations', calendar: 'Calendar rows', reviews: 'Reviews', tasks: 'Tasks',
    };
    const picker = `<div style="display:flex; gap:6px; flex-wrap:wrap; margin-bottom:16px;">` +
      DATA_SOURCES.map(s => {
        const n = counts[s];
        const count = Number.isFinite(Number(n)) ? ` <span class="de-tab-count">${n}</span>` : '';
        return `<button class="de-toggle-btn${this.dataSource === s ? ' active' : ''}" data-de-source="${s}">${labels[s]}${count}</button>`;
      }).join('') + `</div>`;
    let body = '';
    switch (this.dataSource) {
      case 'overview':     body = this.tabOverview(); break;
      case 'airroi':       body = this.tabAirroi(); break;
      case 'pricelabs':    body = this.tabPricelabs(); break;
      case 'reservations': body = this.tabReservations(); break;
      case 'calendar':     body = this.tabCalendar(); break;
      case 'reviews':      body = this.tabReviews(); break;
      case 'tasks':        body = this.tabTasks(); break;
    }
    return picker + body;
  }

  // ── tabs ───────────────────────────────────────────────────
  private tabOverview(): string {
    const l = this.data.listing || {};
    const pl = this.data.pricelabs || {};
    const ar = this.data.airroi || null;
    const col = (title: string, body: string) => `<div class="de-col"><div style="font-size:12px; font-weight:700; color:var(--text); margin-bottom:6px;">${title}</div>${body}</div>`;
    const hostaway = this.kvTable({
      Name: l.name, City: l.city || l.market_locality, Status: l.status,
      Bedrooms: l.bedrooms, Bathrooms: l.bathrooms, 'Max guests': l.max_guests,
      'Min nights': l.min_nights, Rating: l.review_score, 'Mgmt fee %': l.mgmt_fee_percent,
    });
    const plSnap = pl.listing_id ? this.kvTable({
      'Occ next 30': this.pct(pl.occ_next_30), 'Market occ 30': this.pct(pl.market_occ_next_30),
      'ADR next 30': this.money(pl.adr_next_30), 'MPI next 30': this.ratio(pl.mpi_next_30),
      'Base price': this.money(pl.base_price), 'Recommended': this.money(pl.recommended_base_price),
    }) : this.empty('Not linked.');
    const arSnap = ar ? this.kvTable({
      'Expected revenue': this.money(ar.expected), 'Range (p25–p90)': `${this.money(ar.low)} – ${this.money(ar.high)}`,
      'Market occ (p50)': this.pct(ar.occ_pct?.p50 ?? ar.occ), 'Market ADR': this.money(ar.adr),
    }) : this.empty('No AirROI estimate.');
    // The at-a-glance strip (Notes-4: the property window should answer review
    // trend, channel distribution, and last-7-day bookings without tab-hopping).
    const res = (this.data.reservations || []) as any[];
    const sevenAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
    const recent = res.filter(r => String(r.booking_date || '').slice(0, 10) >= sevenAgo);
    const recentGross = recent.reduce((s, r) => s + (parseFloat(r.total || r.total_price) || 0), 0);
    const byChannel: Record<string, number> = {};
    const CH_ALIAS: Record<string, string> = { airbnbofficial: 'airbnb', airbnb: 'airbnb', homeaway: 'vrbo', vrbo: 'vrbo', bookingcom: 'booking', booking: 'booking', direct: 'direct', website: 'direct' };
    for (const r of res) { const raw = (r.channel_name || r.channel || 'other').toString().toLowerCase().replace(/[^a-z]/g, '') || 'other'; const c = CH_ALIAS[raw] || raw; byChannel[c] = (byChannel[c] || 0) + 1; }
    const chTotal = res.length || 1;
    const chChips = Object.entries(byChannel).sort((a, b) => b[1] - a[1]).slice(0, 5)
      .map(([c, n]) => `<span class="channel-badge channel-${['airbnb','vrbo','booking','direct'].includes(c) ? c : 'other'}" style="font-size:10px;">${c} ${Math.round(n / chTotal * 100)}%</span>`).join(' ');
    const revs = (this.data.reviews || []) as any[];
    const rated = revs.filter(r => r.rating != null).map(r => Number(r.rating));
    const recent5 = rated.slice(0, 5);
    const prior = rated.slice(5, 15);
    const avg = (a: number[]) => a.length ? a.reduce((s, x) => s + x, 0) / a.length : null;
    const rAvg = avg(recent5), pAvg = avg(prior);
    const trend = (rAvg != null && pAvg != null) ? (rAvg - pAvg >= 0.3 ? '▲ improving' : pAvg - rAvg >= 0.3 ? '▼ slipping' : '→ steady') : '';
    const glance = (label: string, value: string, sub: string) => `
      <div style="border:1px solid var(--border); border-radius:8px; padding:10px 14px; min-width:150px;">
        <div style="font-size:10px; font-weight:500; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">${label}</div>
        <div class="mono num" style="font-size:18px; font-weight:500; margin-top:2px;">${value}</div>
        <div style="font-size:10px; color:var(--text-faint, #9ca3af); margin-top:1px;">${sub}</div>
      </div>`;
    const strip = `
      <div style="display:flex; gap:10px; flex-wrap:wrap; margin-bottom:16px;">
        ${glance('Review score', l.review_score ? Number(l.review_score).toFixed(2) + '★' : '—', trend ? `last 5: ${rAvg!.toFixed(1)}/10 ${trend}` : 'no recent reviews')}
        ${glance('Bookings · last 7d', String(recent.length), recent.length ? '$' + Math.round(recentGross).toLocaleString() + ' gross' : 'none booked this week')}
        <div style="border:1px solid var(--border); border-radius:8px; padding:10px 14px; flex:1; min-width:200px;">
          <div style="font-size:10px; font-weight:500; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Channel mix · loaded stays</div>
          <div style="margin-top:6px; display:flex; gap:6px; flex-wrap:wrap;">${chChips || '<span style="font-size:11px; color:var(--text-faint, #9ca3af);">no reservations loaded</span>'}</div>
        </div>
      </div>`;
    return strip + `<div class="de-cols">
      ${col('🏠 Hostaway', hostaway)}
      ${col('📊 PriceLabs (next 30)', plSnap)}
      ${col('🌐 AirROI (annual)', arSnap)}
    </div>`;
  }

  private tabAirroi(): string {
    const ar = this.data.airroi;
    if (!ar) return this.empty('No AirROI estimate cached for this property. Generate it on the Projections page.');
    const op = ar.occ_pct || {};
    const kvRow = (k: string, v: string) => `<tr style="border-bottom:1px solid var(--border-soft);"><td style="padding:7px 12px; font-size:13px;">${k}</td><td style="padding:7px 12px; text-align:right; font-weight:600;">${v}</td></tr>`;
    const revRows = ([['p25 (low)', ar.low], ['Expected', ar.expected], ['p90 (high)', ar.high]] as [string, any][])
      .map(([k, v]) => kvRow(k, this.money(v))).join('');
    const occRows = ([['p25', op.p25], ['p50 (median)', op.p50], ['p75', op.p75], ['p90', op.p90], ['Average', op.avg]] as [string, any][])
      .map(([k, v]) => kvRow(k, this.pct(v))).join('');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthly = Array.isArray(ar.monthly) && ar.monthly.length === 12 ? this.tableWrap(`
        <thead><tr>${this.th('Month')}${this.th('% of annual', 'right')}${this.th('Est. revenue', 'right')}</tr></thead>
        <tbody>${ar.monthly.map((f: number, i: number) => {
          const frac = Number(f);
          return `<tr style="border-bottom:1px solid var(--border-soft);">
          <td style="padding:6px 12px; font-size:13px;">${monthNames[i]}</td>
          <td style="padding:6px 12px; text-align:right;">${Number.isFinite(frac) ? (frac * 100).toFixed(1) + '%' : '—'}</td>
          <td style="padding:6px 12px; text-align:right;">${this.money(Number.isFinite(frac) ? (ar.expected || 0) * frac : NaN)}</td></tr>`;
        }).join('')}</tbody>`) : this.empty('No seasonality data.');
    const t = (title: string, body: string) => `<div class="de-col">${this.section(title, body)}</div>`;
    return `
      <div class="de-cols">
        ${t('Annual revenue percentiles', `<div class="u-table-wrap"><table style="width:100%; border-collapse:collapse;"><tbody>${revRows}</tbody></table></div>`)}
        ${t('Occupancy percentiles', `<div class="u-table-wrap"><table style="width:100%; border-collapse:collapse;"><tbody>${occRows}</tbody></table></div>`)}
        ${t('Market', this.kvTable({ 'Market ADR': this.money(ar.adr), 'Market occupancy': this.pct(ar.occ), Generated: ar._generated_at ? new Date(ar._generated_at).toLocaleDateString() : '—' }))}
      </div>
      ${this.section('Monthly seasonality (revenue distribution)', monthly)}`;
  }

  private tabPricelabs(): string {
    const pl = this.data.pricelabs;
    if (!pl) return this.empty('No PriceLabs data linked for this property.');
    const windows = [
      { k: '7', label: 'Next 7' }, { k: '30', label: 'Next 30' }, { k: '60', label: 'Next 60' }, { k: '90', label: 'Next 90' },
    ];
    const row = (label: string, fn: (k: string) => string, past: string) => `<tr style="border-bottom:1px solid var(--border-soft);">
      <td style="padding:7px 12px; font-size:13px; font-weight:500; white-space:nowrap;">${label}</td>
      ${windows.map(w => `<td style="padding:7px 12px; text-align:right;">${fn(w.k)}</td>`).join('')}
      <td style="padding:7px 12px; text-align:right; color:var(--text-muted);">${past}</td></tr>`;
    const windowTable = this.tableWrap(`
        <thead><tr>${this.th('Metric')}${windows.map(w => this.th(w.label, 'right')).join('')}${this.th('Past 30', 'right')}</tr></thead>
        <tbody>
          ${row(`Occupancy ${infoIcon('occupancy')}`, k => this.pct(pl[`occ_next_${k}`]), this.pct(pl.occ_past_30))}
          ${row('Market occ', k => this.pct(pl[`market_occ_next_${k}`]), '—')}
          ${row(`ADR ${infoIcon('adr')}`, k => this.money(pl[`adr_next_${k}`]), this.money(pl.adr_past_30))}
          ${row(`RevPAR ${infoIcon('revpar')}`, k => this.money(pl[`revpar_next_${k}`]), this.money(pl.revpar_past_30))}
          ${row(`MPI ${infoIcon('mpi')}`, k => this.ratio(pl[`mpi_next_${k}`]), '—')}
        </tbody>`);
    const pricing = this.kvTable({ 'Base price': this.money(pl.base_price), 'Recommended base': this.money(pl.recommended_base_price), 'Synced at': pl.synced_at });
    return `
      ${this.section('Pricing windows', windowTable)}
      ${this.section(`Pricing ${infoIcon('all_in_price')}`, pricing)}
      ${this.section('All PriceLabs fields (raw)', `<details><summary style="cursor:pointer; font-size:12px; color:var(--text-muted);">Show all ${Object.keys(pl).length} fields</summary><div style="margin-top:8px;">${this.kvTable(pl)}</div></details>`)}`;
  }

  private tabReservations(): string {
    const rows = this.data.reservations || [];
    if (!rows.length) return this.empty('No reservations in the last 12 months.');
    const body = rows.slice().reverse().map((r: any) => `<tr style="border-bottom:1px solid var(--border-soft);">
      <td style="padding:6px 12px; font-size:13px; white-space:nowrap;">${this.cell(r.check_in)}</td>
      <td style="padding:6px 12px; font-size:13px; white-space:nowrap;">${this.cell(r.check_out)}</td>
      <td style="padding:6px 12px; text-align:center;">${this.num(r.nights)}</td>
      <td style="padding:6px 12px; font-size:13px;">${this.cell(r.guest_name)}</td>
      <td style="padding:6px 12px; font-size:13px;">${this.cell(r.channel_name || r.channel)}</td>
      <td style="padding:6px 12px; text-align:right;">${this.money(r.total || r.total_price)}</td>
      <td style="padding:6px 12px; text-align:center;">${this.num(r.lead_time_days)}</td>
      <td style="padding:6px 12px; font-size:12px; color:var(--text-muted);">${this.cell(r.status)}</td></tr>`).join('');
    return this.tableWrap(
      `<thead><tr>${['Check-in', 'Check-out'].map(t => this.th(t)).join('')}${this.th('Nights', 'center')}${this.th('Guest')}${this.th('Channel')}${this.th('Gross', 'right')}${this.th('Lead', 'center')}${this.th('Status')}</tr></thead><tbody>${body}</tbody>`,
      '560px');
  }

  // A real calendar, not a list (Notes-4): 7-column month grid — booked nights
  // gray with a check, blocked dashed, open shows price + min-stay.
  private tabCalendar(): string {
    const rows = (this.data.calendar || []).slice().sort((a: any, b: any) => String(a.date).localeCompare(String(b.date)));
    if (!rows.length) return this.empty('No calendar data.');
    const legend = `
      <div style="display:flex; gap:14px; flex-wrap:wrap; font-size:11px; color:var(--text-muted); margin-bottom:10px;">
        <span><span style="display:inline-block; width:10px; height:10px; border-radius:3px; background:var(--bg-subtle); border:1px solid var(--border); vertical-align:-1px;"></span> booked ✓</span>
        <span><span style="display:inline-block; width:10px; height:10px; border-radius:3px; border:1px dashed var(--border); vertical-align:-1px;"></span> blocked</span>
        <span><span style="display:inline-block; width:10px; height:10px; border-radius:3px; background:#fff; border:1px solid var(--border); vertical-align:-1px;"></span> open — nightly price · min-stay</span>
      </div>`;
    const firstDow = new Date(rows[0].date + 'T00:00:00').getDay();
    const cells: string[] = Array.from({ length: firstDow }, () => '<div></div>');
    let lastMonth = '';
    for (const d of rows) {
      const day = new Date(d.date + 'T00:00:00');
      const mon = day.toLocaleDateString('en-US', { month: 'short' });
      const monthTag = mon !== lastMonth ? `<div style="font-size:9px; color:var(--text-muted); text-transform:uppercase;">${mon}</div>` : '';
      lastMonth = mon;
      const booked = d.status === 'reserved' || d.status === 'booked';
      const blocked = !booked && (d.is_available === false || d.status === 'unavailable');
      if (booked) {
        cells.push(`<div style="border:1px solid var(--border-soft); border-radius:6px; padding:5px 6px; background:var(--bg-subtle); color:var(--text-muted); min-height:50px;">${monthTag}<div class="mono" style="font-size:11px;">${day.getDate()}</div><div style="font-size:10px;">booked ✓</div></div>`);
      } else if (blocked) {
        cells.push(`<div style="border:1px dashed var(--border); border-radius:6px; padding:5px 6px; color:var(--text-faint, #9ca3af); min-height:50px;">${monthTag}<div class="mono" style="font-size:11px;">${day.getDate()}</div><div style="font-size:10px;">blocked</div></div>`);
      } else {
        cells.push(`<div style="border:1px solid var(--border); border-radius:6px; padding:5px 6px; background:#fff; min-height:50px;">${monthTag}
          <div class="mono" style="font-size:11px;">${day.getDate()}</div>
          <div class="mono num" style="font-size:10px; color:var(--text);">${d.price ? '$' + Math.round(Number(d.price)) : '—'}</div>
          ${d.min_stay ? `<div class="mono" style="font-size:9px; color:var(--text-faint, #9ca3af);">min ${d.min_stay}n</div>` : ''}
        </div>`);
      }
    }
    return legend + `
      <div style="display:grid; grid-template-columns:repeat(7,1fr); gap:4px; font-size:10px; color:var(--text-muted); margin-bottom:4px;">${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(x => `<div style="text-align:center;">${x}</div>`).join('')}</div>
      <div style="display:grid; grid-template-columns:repeat(7,1fr); gap:4px;">${cells.join('')}</div>`;
  }

  private tabReviews(): string {
    const rows = this.data.reviews || [];
    if (!rows.length) return this.empty('No published guest reviews for this listing yet.');
    const rated = rows.filter((r: any) => r.rating != null && Number.isFinite(Number(r.rating)));
    const avg = rated.length ? rated.reduce((s: number, r: any) => s + Number(r.rating), 0) / rated.length : null;
    const low = rated.filter((r: any) => Number(r.rating) < 8).length;
    const header = `
      <div style="display:flex; gap:24px; flex-wrap:wrap; margin-bottom:12px; padding-bottom:10px; border-bottom:1px solid var(--border);">
        <div><div style="font-size:10px; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Guest rating</div><div class="mono num" style="font-size:20px; font-weight:500;">${avg != null ? avg.toFixed(1) + '/10' : '—'}</div></div>
        <div><div style="font-size:10px; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Reviews shown</div><div class="mono num" style="font-size:20px; font-weight:500;">${rows.length}</div></div>
        <div><div style="font-size:10px; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Under 8/10</div><div class="mono num" style="font-size:20px; font-weight:500; color:${low ? 'var(--danger)' : 'var(--success)'};">${low}</div></div>
        <div style="align-self:end; font-size:11px; color:var(--text-faint, #9ca3af);">guest-to-host · published · newest first · Hostaway scale is /10</div>
      </div>`;
    return header + rows.map((r: any) => `<div style="padding:10px 0; border-bottom:1px solid var(--border-soft);">
      <div style="display:flex; justify-content:space-between; gap:12px; font-size:13px; font-weight:600;">
        <span class="u-truncate" style="min-width:0;">${this.cell(r.reviewer_name || 'Guest')}</span>
        <span style="white-space:nowrap; color:${r.rating == null ? 'var(--text-muted)' : Number(r.rating) < 8 ? 'var(--danger)' : 'var(--success)'};">${r.rating != null && Number.isFinite(Number(r.rating)) ? r.rating + '/10' : 'no rating'}</span>
      </div>
      <div style="font-size:11px; color:var(--text-muted); margin:2px 0;">${this.cell(r.departure_date || r.submitted_at)}</div>
      ${r.public_review ? `<div style="font-size:13px; color:var(--text-muted); line-height:1.5;">${Formatters.escapeHtml(r.public_review)}</div>` : ''}
    </div>`).join('');
  }

  private tabTasks(): string {
    const rows = this.data.tasks || [];
    if (!rows.length) return this.empty('No tasks.');
    const body = rows.map((t: any) => `<tr style="border-bottom:1px solid var(--border-soft);">
      <td style="padding:6px 12px; font-size:13px;">${this.cell(t.title)}</td>
      <td style="padding:6px 12px; font-size:12px;">${this.cell(t.status)}</td>
      <td style="padding:6px 12px; font-size:12px;">${this.cell(t.category)}</td>
      <td style="padding:6px 12px; font-size:12px;">${this.cell(t.should_end_by)}</td>
      <td style="padding:6px 12px; text-align:right;">${this.money(t.cost)}</td></tr>`).join('');
    return this.tableWrap(
      `<thead><tr>${this.th('Task')}${this.th('Status')}${this.th('Category')}${this.th('Due')}${this.th('Cost', 'right')}</tr></thead><tbody>${body}</tbody>`);
  }

  // ── analysis helpers ───────────────────────────────────────
  private goalColor(p: number): string { return p >= 100 ? 'var(--success-text)' : p >= 80 ? 'var(--warning-text)' : 'var(--danger)'; }
  private ppBadge(pp: number | null): string {
    if (pp == null) return '';
    const c = pp > 0 ? 'var(--success-text)' : pp < 0 ? 'var(--danger)' : 'var(--text-muted)';
    const s = pp > 0 ? '+' : '';
    return `<span style="color:${c}; font-size:11px; font-weight:600;">${s}${pp}pp</span>`;
  }
  private analysisControls(): string {
    const yr = this.aYear;
    const pctBtn = (p: number) => `<button data-pct="${p}" style="padding:3px 9px; border-radius:var(--radius-sm); font-size:11px; cursor:pointer; border:1px solid ${p === this.marketPct ? 'var(--accent)' : 'var(--border)'}; background:${p === this.marketPct ? 'var(--accent)' : 'var(--card)'}; color:${p === this.marketPct ? '#fff' : 'var(--text)'};">p${p}</button>`;
    const yrBtn = (y: number) => `<button data-ayear="${y}" style="padding:3px 9px; border-radius:var(--radius-sm); font-size:11px; cursor:pointer; border:1px solid ${y === yr ? 'var(--accent)' : 'var(--border)'}; background:${y === yr ? 'var(--accent)' : 'var(--card)'}; color:${y === yr ? '#fff' : 'var(--text)'};">${y}</button>`;
    // MTR units don't follow STR market seasonality — the projection is from the
    // unit's own rent history, and STR market comparisons (MPI/RevPAR index) are
    // suppressed. Say so plainly so a $0/'no basis' month doesn't read as a miss.
    const mtrBanner = this.analysis?.meta?.mtr
      ? `<div style="font-size:11.5px; color:var(--text-muted); background:var(--bg-subtle); border:1px solid var(--border); border-radius:var(--radius-sm); padding:7px 11px; margin-bottom:10px;">
           <strong style="color:var(--text);">Mid-term rental.</strong> Projected from this unit's own contracted-rent history (same month last year), not STR market seasonality. Months with no comparable lease history show no goal rather than an invented STR number; STR market comparisons (MPI, RevPAR index) don't apply.
         </div>`
      : '';
    return mtrBanner + `<div style="display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; margin-bottom:14px;">
      <div style="font-size:12px; color:var(--text-muted);">Comparing against <strong style="color:var(--text);">p${this.marketPct}</strong> of market${this.analysis?.meta?.has_airroi ? '' : ' · <span style="color:var(--warning-text);">no AirROI estimate for this property</span>'}</div>
      <div style="display:flex; gap:10px; align-items:center;">
        <span style="font-size:11px; color:var(--text-muted);">view as</span> ${[25, 50, 75, 90].map(pctBtn).join(' ')}
        <span style="font-size:11px; color:var(--text-muted); margin-left:8px;">year</span> ${[yr - 1, yr, yr + 1].filter(y => y >= 2023 && y <= new Date().getFullYear() + 1).map(yrBtn).join(' ')}
      </div>
    </div>`;
  }

  // ── 🎯 Pacing & Pricing ────────────────────────────────────
  private tabPacing(): string {
    const months = this.analysis?.pacing?.months || [];
    if (!months.length) return this.analysisControls() + '<div style="color:var(--text-muted); font-size:13px;">No pacing data.</div>';
    const card = (m: any, focused: boolean) => {
      const row = (label: string, val: string, extra = '') => `<div style="display:flex; justify-content:space-between; gap:8px; font-size:12px; padding:3px 0; border-top:1px solid var(--border-soft);"><span style="color:var(--text-muted);">${label}</span><span style="text-align:right;">${val} ${extra}</span></div>`;
      const mk = `${String(m.year)}-${String(m.month).padStart(2, '0')}`;
      const occW = this.barPct(m.occupancy_pct);
      return `<div style="flex:1; min-width:250px; border:1px solid ${focused ? 'var(--accent)' : 'var(--border)'}; border-radius:var(--radius-lg); overflow:hidden;">
        <div style="height:8px; background:var(--bg-subtle);"><div style="height:100%; width:${occW}%; background:var(--success);"></div></div>
        <div style="padding:12px 14px;">
          <div style="display:flex; justify-content:space-between; align-items:center; gap:8px;">
            <span style="font-weight:700; font-size:14px;">${m.label}</span>
            ${focused ? '<span style="font-size:10px; background:var(--accent-soft); color:var(--accent-hover); padding:1px 7px; border-radius:10px;">focused</span>' : `<span style="font-size:13px; color:var(--text-muted);">${this.pct(m.occupancy_pct)}</span>`}
          </div>
          <div style="margin:6px 0 2px; font-size:15px; font-weight:700;">${this.money(m.revenue)} <span style="color:var(--text-muted); font-weight:400;">/ ${this.money(m.goal)} ${infoIcon('goal')}</span>
            <span style="color:${this.goalColor(m.actual_vs_goal_pct)}; font-size:13px;">${this.pct(m.actual_vs_goal_pct)} ${infoIcon('pace_vs_goal')}</span>
            <button data-goal-edit="${mk}" data-goal-cur="${m.goal_is_override ? m.goal : ''}" title="Set goal" style="border:none; background:none; cursor:pointer; font-size:12px;">✏️</button>
          </div>
          <div style="font-size:11px; color:var(--text-muted); margin-bottom:8px;">${this.num(m.booked_nights)}/${this.num(m.nights_in_month)} nights · ADR ${this.money(m.adr)}${m.goal_is_override ? ' · goal: manual' : ''}</div>
          ${row('Occ vs LY', `${this.pct(m.occupancy_pct)} vs ${this.pct(m.ly_occupancy_pct)}`, this.ppBadge(m.occ_delta_pp))}
          ${row(`Mkt ADR ${infoIcon('adr')}`, this.money(m.market_adr))}
          ${row(`RevPAR (acct / mkt) ${infoIcon('revpar')}`, `${this.money(m.revpar)} / ${this.money(m.market_revpar)}`)}
          ${row('Revenue vs LY', `${this.money(m.revenue)} vs ${this.money(m.ly_revenue)}`)}
          ${row('Booking window', m.booking_window_days != null ? `${this.num(m.booking_window_days)}d` : '—')}
          ${row(`MPI ${infoIcon('mpi')}`, this.ratio(m.mpi))}
        </div>
      </div>`;
    };
    const f = months[1] || months[0];
    const focusBar = `
      <div style="border:1px solid var(--border); border-radius:var(--radius-lg); padding:14px 16px; margin-top:16px;">
        <div style="display:flex; justify-content:space-between; gap:10px; font-size:13px; flex-wrap:wrap;"><strong>${f.label}</strong><span style="color:var(--text-muted);">${this.num(f.booked_nights)}/${this.num(f.nights_in_month)} nights · ${this.money(f.revenue)}</span></div>
        <div style="display:flex; align-items:center; gap:10px; margin:8px 0; flex-wrap:wrap;">
          <span style="font-size:13px;">🎯 Goal ${infoIcon('goal')} <strong>${this.money(f.goal)}</strong> · Actual <strong>${this.money(f.revenue)}</strong> <span style="color:${this.goalColor(f.actual_vs_goal_pct)};">${this.pct(f.actual_vs_goal_pct)} ${infoIcon('pace_vs_goal')}</span></span>
        </div>
        <div style="height:14px; border-radius:7px; background:var(--bg-subtle); overflow:hidden;"><div style="height:100%; width:${this.barPct(f.actual_vs_goal_pct)}%; background:var(--warning);"></div></div>
        <div style="display:flex; gap:18px; font-size:12px; color:var(--text-muted); margin-top:10px; flex-wrap:wrap;">
          <span>Occ pacing <strong style="color:var(--text);">${this.pct(f.occupancy_pct)}</strong> (LY ${this.pct(f.ly_occupancy_pct)}) ${this.ppBadge(f.occ_delta_pp)}</span>
          <span>Mkt Occ <strong style="color:var(--text);">${f.market_occupancy_pct != null ? this.pct(f.market_occupancy_pct) : '—'}</strong></span>
          <span>Mkt RevPAR ${infoIcon('revpar')} <strong style="color:var(--text);">${this.money(f.market_revpar)}</strong></span>
          <span>MPI ${infoIcon('mpi')} <strong style="color:var(--text);">${this.ratio(f.mpi)}</strong></span>
          <span>RevPAR index <strong style="color:var(--text);">${this.ratio(f.revpar_index)}</strong></span>
        </div>
      </div>`;
    return this.analysisControls()
      + `<div style="display:flex; gap:14px; flex-wrap:wrap;">${months.map((m: any, i: number) => card(m, i === 1)).join('')}</div>`
      + focusBar;
  }

  // ── 📈 Forecast vs Actual ──────────────────────────────────
  private tabForecast(controls = true): string {
    const fc = this.analysis?.forecast;
    const head = controls ? this.analysisControls() : '';
    if (!fc) return head + '<div style="color:var(--text-muted); font-size:13px;">No forecast data.</div>';
    const stat = (label: string, val: string, sub = '') => `<div style="min-width:120px;"><div style="font-size:11px; color:var(--text-muted); text-transform:uppercase;">${label}</div><div style="font-size:18px; font-weight:700;">${val}</div>${sub ? `<div style="font-size:11px; color:var(--text-muted);">${sub}</div>` : ''}</div>`;
    const ytdPct = fc.ytd_goal > 0 ? Math.round(fc.ytd_actual / fc.ytd_goal * 100) : 0;
    const floorPct = fc.year_goal > 0 ? Math.round(fc.full_year_floor / fc.year_goal * 100) : 0;
    const rows = (fc.months || []).map((m: any) => {
      const tag = m.is_past ? '' : m.is_current ? ' <span style="font-size:10px; color:var(--accent-hover);">(current)</span>' : ' <span style="font-size:10px; color:var(--info);">(future)</span>';
      return `<tr style="border-bottom:1px solid var(--border-soft); ${m.is_current ? 'background:var(--accent-soft);' : ''}">
        <td style="padding:7px 12px; font-size:13px;">${m.label}${tag}</td>
        <td style="padding:7px 12px; text-align:right;">${this.money(m.projected)}${m.goal_is_override ? ' ✏️' : ''}</td>
        <td style="padding:7px 12px; text-align:right; font-weight:600;">${this.money(m.actual)}</td>
        <td style="padding:7px 12px; text-align:right; color:${this.goalColor(m.variance_pct)}; font-weight:600;">${this.pct(m.variance_pct)}</td>
        <td style="padding:7px 12px; text-align:right; color:var(--text-muted); font-size:11px;">${this.cell(m.actual_label)}</td>
        <td style="padding:7px 12px; text-align:center;">${this.pct(m.occupancy_pct)}</td></tr>`;
    }).join('');
    return head
      + `<div style="display:flex; gap:24px; flex-wrap:wrap; margin-bottom:16px;">
          ${stat(`Year goal ${infoIcon('goal')}`, this.money(fc.year_goal))}
          ${stat('YTD actual', this.money(fc.ytd_actual), `${ytdPct}% of YTD goal`)}
          ${stat('Forward on-the-books', this.money(fc.fwd_otb), `of ${this.money(fc.fwd_goal)} goal`)}
          ${stat('Full-year floor', this.money(fc.full_year_floor), `${floorPct}% of year goal`)}
        </div>
        ${this.tableWrap(`
          <thead><tr>${this.th('Month')}${this.th(`Goal ${infoIcon('goal')}`, 'right')}${this.th('Actual', 'right')}${this.th(`% ${infoIcon('pace_vs_goal')}`, 'right')}${this.th('Basis')}${this.th(`Occ ${infoIcon('occupancy')}`, 'center')}</tr></thead>
          <tbody>${rows}</tbody>`)}`;
  }

  // ── 💰 Financials ──────────────────────────────────────────
  private tabFinancials(): string {
    const fin = this.analysis?.financials;
    if (!fin) return this.analysisControls() + '<div style="color:var(--text-muted); font-size:13px;">No financials.</div>';
    const rows = (fin.months || []).map((m: any) => `<tr style="border-bottom:1px solid var(--border-soft);">
      <td style="padding:7px 12px; font-size:13px;">${m.label}</td>
      <td style="padding:7px 12px; text-align:center;">${this.num(m.nights)}</td>
      <td style="padding:7px 12px; text-align:right;">${this.money(m.gross)}</td>
      <td style="padding:7px 12px; text-align:right; color:var(--warning-text);">${this.money(m.mgmt_fee)}</td>
      <td style="padding:7px 12px; text-align:right; color:var(--success-text); font-weight:600;">${this.money(m.owner_net)}</td></tr>`).join('');
    return this.analysisControls()
      + `<div style="font-size:12px; color:var(--text-muted); margin-bottom:8px;">${fin.fee_source === 'engine'
          ? `Fee engine (effective rate ${this.pct(fin.fee_pct)}) · each booking's formula-computed gross / mgmt fee / owner net allocated per stay night — matches owner statements and revenue metrics.`
          : `Flat management fee ${this.pct(fin.fee_pct)} (no fee-engine data for this property yet) · gross = booked revenue allocated per stay night · owner net = gross − mgmt fee (simplified).`}</div>
        ${this.tableWrap(`
          <thead><tr>${this.th('Month')}${this.th('Nights', 'center')}${this.th('Gross', 'right')}${this.th('Mgmt fee', 'right')}${this.th('Owner net', 'right')}</tr></thead>
          <tbody>${rows}</tbody>
          <tfoot><tr style="background:var(--bg-subtle); font-weight:700;">
            <td style="padding:8px 12px;">TOTAL</td><td></td>
            <td style="padding:8px 12px; text-align:right;">${this.money(fin.total_gross)}</td>
            <td style="padding:8px 12px; text-align:right; color:var(--warning-text);">${this.money(fin.total_mgmt_fee)}</td>
            <td style="padding:8px 12px; text-align:right; color:var(--success-text);">${this.money(fin.total_owner_net)}</td></tr></tfoot>`)}`;
  }

  // ── 📄 Report (trailing 30 days) ───────────────────────────
  private tabReport(): string {
    // The branded report IS the report — same artifact as Reports → Single
    // property, RM voice by default here in the cockpit.
    return '<div id="de-report-host"><div style="padding:24px; text-align:center; color:var(--text-muted); font-size:13px;">Building the branded report…</div></div>';
  }

  private reportDocInstance: any = null;
  private async mountReportDoc(): Promise<void> {
    const host = document.getElementById('de-report-host');
    if (!host || !this.selectedId) return;
    const { PropertyReportDoc } = await import('../shared/components/PropertyReportDoc');
    this.reportDocInstance?.destroy?.();
    this.reportDocInstance = new PropertyReportDoc(this.selectedId, 'rm');
    await this.reportDocInstance.render(host);
  }
}
